import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { t } from "i18next";
import { api } from "../../services/api/api";
import { showApiErrorToast } from "../../services/api/error";
import { formatLocalDate } from "../../services/export/timezoneUtils";
import Loading from "../../components/loading/Loading";
import { Card, Typography, IconButton, Tooltip } from "@material-tailwind/react";
import { Breadcrumb } from "../../components/breadcrumb/Breadcrumb";
import { Table } from "../../components/table/Table";
import EditCreateDrawer from "../../components/edit-create-drawer/EditCreateDrawer";
import ReplenishmentAssignmentForm from "./ReplenishmentAssignmentForm";
import ReplenishmentStatusBadge from "../../components/data/replenishment-status/ReplenishmentStatusBadge";
import ReplenishmentPriorityBadge from "../../components/data/replenishment-priority/ReplenishmentPriorityBadge";
import { exportReplenishmentPartsToCSV } from "../../services/export/data/replenishmentExport";
import {
  PaperAirplaneIcon,
  PrinterIcon,
  XCircleIcon,
  TableCellsIcon,
  ClipboardDocumentCheckIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import "./Replenishment.scss";
import { ChatBubbleBottomCenterTextIcon } from "@heroicons/react/24/outline";

export function Replenishment() {
  const { id } = useParams();
  const [replenishment, setReplenishment] = useState<EasyTech.Replenishment | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [breadcrumbPath, setBreadcrumbPath] = useState<{name: string, path: string}[]>([]);

  /**
   * Charge les détails de la demande
   */
  useEffect(() => {
    const loadReplenishment = async () => {
      if (!id) return;
      
      try {
        setIsLoading(true);
        const response = await api.get(`users/replenishments/${id}`);
        setReplenishment(response as unknown as EasyTech.Replenishment);
      } catch (error) {
        showApiErrorToast(error);
      } finally {
        setIsLoading(false);
      }
    };

    loadReplenishment();
  }, [id]);

  /**
   * Met à jour le breadcrumb
   */
  useEffect(() => {
    if (replenishment) {
      setBreadcrumbPath([{
        name: "replenishments",
        path: "/replenishments"
      }, {
        name: replenishment.ref || `#${replenishment.id.slice(0, 8)}`,
        path: `/replenishments/${replenishment.id}`
      }]);
    }
  }, [replenishment]);

  /**
   * Export CSV des pièces
   */
  const handleExportCSV = () => {
    if (replenishment) {
      exportReplenishmentPartsToCSV(replenishment);
    }
  };

  /**
   * Imprimer la page
   */
  const handlePrint = () => {
    window.print();
  };

  /**
   * Changer le statut de la demande
   * Workflow:
   * - CREATED → SENT ou CANCELLED
   * - SENT → INVENTORIED ou retour à CREATED
   * - INVENTORIED ou CANCELLED → aucun changement
   */
  const handleStatusChange = async (newStatus: EasyTech.ReplenishmentStatus) => {
    if (!replenishment) return;
    
    try {
      setIsUpdating(true);
      await api.put(`users/replenishments/${replenishment.id}/status/${newStatus}`, {});
      
      setReplenishment({
        ...replenishment,
        status: newStatus
      });
      
      toast.success(t("replenishment:messages.statusUpdated"));
    } catch (error) {
      showApiErrorToast(error);
    } finally {
      setIsUpdating(false);
    }
  };

  /**
   * Éditer un replenishment (changements simples)
   * Gère : priorité, pièces, commentaires
   * Les champs technician, assignedTechnician, van sont immutables
   */
  const handleEditReplenishment = async (formData: any): Promise<void> => {
    if (!replenishment) return;
    
    try {
      setIsUpdating(true);

      // 1️⃣ METTRE À JOUR LES CHAMPS PRINCIPAUX (priority, parts, comments)
      const updatePayload: any = {
        technician: { id: replenishment.technician.id }, // Demandeur (immutable)
        priority: formData.priority || replenishment.priority,
        comments: formData.comments !== undefined ? formData.comments : replenishment.comments
      };

      // Inclure les pièces si fournies
      if (formData.parts && Array.isArray(formData.parts)) {
        updatePayload.parts = formData.parts.map((p: any) => ({
          part: { id: p.part.id },
          quantity: p.quantity
        }));
      } else {
        updatePayload.parts = replenishment.parts.map((p: any) => ({
          part: { id: p.part.id },
          quantity: p.quantity
        }));
      }

      console.log("📤 UPDATE principal - Payload:", updatePayload);
      await api.put(`users/replenishments/${replenishment.id}`, updatePayload);
      toast.success(t("replenishment:messages.replenishmentUpdated"));

      // 🔄 RECHARGER LES DONNÉES
      const response = await api.get(`users/replenishments/${replenishment.id}`);
      setReplenishment(response as unknown as EasyTech.Replenishment);

    } catch (error) {
      console.error("❌ Erreur lors de l'édition du replenishment:", error);
      showApiErrorToast(error);
      throw error;
    } finally {
      setIsUpdating(false);
    }
  };

  if (isLoading) {
    return <Loading />;
  }

  if (!replenishment) {
    return (
      <div className="text-center py-8">
        <p className="text-lg text-blue-gray-700">
          {t("replenishment:messages.noData")}
        </p>
      </div>
    );
  }

  return (
    <div className="replenishment-detail">
      <Breadcrumb path={breadcrumbPath} />

      {/* Header avec titre et actions */}
      <div className="replenishment-header">
        <div className="header-title">
          <Typography variant="h2">
            {t("replenishment:title")} {replenishment.ref || `#${replenishment.id.slice(0, 8)}`}
          </Typography>
        </div>

        <div className="header-status"> 
          <ReplenishmentStatusBadge status={replenishment.status} />
        </div>

        <div className="header-actions">
          {/* Bouton Mark as Sent - only from CREATED */}
          {replenishment.status === "CREATED" && (
            <Tooltip content={t("replenishment:actions.markAsSent")}>
              <IconButton
                onClick={() => handleStatusChange("SENT")}
                disabled={isUpdating}
                style={{ backgroundColor: "#41A1FF" }}
                className="action-button hover:opacity-90"
              >
                <PaperAirplaneIcon className="h-5 w-5 text-white" />
              </IconButton>
            </Tooltip>
          )}

          {/* Bouton Mark as Inventoried - only from SENT */}
          {replenishment.status === "SENT" && (
            <>
            <Tooltip content={t("replenishment:actions.markInProgress")}>
                <IconButton
                  onClick={() => handleStatusChange("CREATED")}
                  disabled={isUpdating}
                  style={{ backgroundColor: "#FA3A9B" }}
                  className="action-button hover:opacity-90"
                >
                  <ChatBubbleBottomCenterTextIcon className="h-5 w-5 text-white" />
                </IconButton>
              </Tooltip>

              <Tooltip content={t("replenishment:actions.markAsInventoried")}>
                <IconButton
                  onClick={() => handleStatusChange("INVENTORIED")}
                  disabled={isUpdating}
                  style={{ backgroundColor: "#2DBA43" }}
                  className="action-button hover:opacity-90"
                >
                  <ClipboardDocumentCheckIcon className="h-5 w-5 text-white" />
                </IconButton>
              </Tooltip>
            </>
          )}

          {/* Bouton Cancel - only from CREATED */}
          {replenishment.status === "CREATED" && (
            <Tooltip content={t("replenishment:actions.markCancelled")}>
              <IconButton
                onClick={() => handleStatusChange("CANCELLED")}
                disabled={isUpdating}
                style={{ backgroundColor: "#9237FF" }}
                className="action-button hover:opacity-90"
              >
                <XCircleIcon className="h-5 w-5 text-white" />
              </IconButton>
            </Tooltip>
          )}

          {/* Bouton Print */}
          <Tooltip content={t("replenishment:actions.print")}>
            <IconButton
              onClick={handlePrint}
              className="action-button bg-white hover:bg-gray-100"
            >
              <PrinterIcon className="h-5 w-5 text-gray-600" />
            </IconButton>
          </Tooltip>

          {/* Bouton Export CSV */}
          <Tooltip content={t("replenishment:actions.exportCsv")}>
            <IconButton
              onClick={handleExportCSV}
              className="action-button bg-white hover:bg-gray-100"
            >
              <TableCellsIcon className="h-5 w-5 text-gray-600" />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      {/* Section General - Single Card with 2 columns */}
      <div className="flex justify-between items-center">
        <Typography variant="h4" className="section-title">
          {t("client:tabs:informations")}
        </Typography>
        {/* Bloquer l'édition pour les statuts CANCELLED, INVENTORIED et SENT */}
        {!["CANCELLED", "INVENTORIED", "SENT"].includes(replenishment.status) && (
          <EditCreateDrawer
                    data={replenishment}
                    FormComponent={ReplenishmentAssignmentForm}
                    isNotCreatable={true}
                    onSubmit={handleEditReplenishment}
                    entityName={t("replenishment:title")}
                    editButtonText={t("common:edit")}
                  />
        )}
      </div>
      

      <Card className="info-card">
        <div className="grid grid-cols-2 gap-6">
          {/* Colonne gauche */}
          <div>
            {/* Created BY */}
            {replenishment.requestFrom && (
              <div className="field">
                <label>{t("replenishment:fields.requestFrom")}</label>
                <Typography>{replenishment.requestFrom.firstName} {replenishment.requestFrom.lastName}</Typography>
              </div>
            )}

            {/* Created ON */}
            <div className="field">
              <label>{t("replenishment:fields.created")}</label>
              <Typography>
                {formatLocalDate(new Date(replenishment.created).toISOString(), localStorage.getItem("realm") || "default")}
              </Typography>
            </div>

            {/* Restock (VAN) */}
            <div className="field">
              <label>{t("replenishment:fields.vanToRestock")}</label>
              <Typography>
                {replenishment.van ? replenishment.van.name : "-"}
              </Typography>
            </div>

            {/* Priority */}
            <div className="field">
              <label>{t("replenishment:fields.priority")}</label>
              <ReplenishmentPriorityBadge priority={replenishment.priority} />
            </div>
          </div>

          {/* Colonne droite */}
          <div>
            {/* Created FOR */}
            <div className="field">
              <label>{t("replenishment:fields.createdBy")}</label>
              <Typography>{replenishment.technician.firstName} {replenishment.technician.lastName}</Typography>
            </div>

            {/* Updated ON */}
            <div className="field">
              <label>{t("replenishment:fields.updated")}</label>
              <Typography>
                {formatLocalDate(new Date(replenishment.updated).toISOString(), localStorage.getItem("realm") || "default")}
              </Typography>
            </div>

            {/* Send TO */}
            <div className="field">
              <label>{t("replenishment:fields.sentTo")}</label>
              <div className="flex justify-between items-center">
                <Typography>
                  {replenishment.assignedTechnician
                    ? `${replenishment.assignedTechnician.firstName} ${replenishment.assignedTechnician.lastName}`
                    : "-"
                  }
                </Typography>
              </div>
            </div>
          </div>
        </div>

        {/* Comments - en bas en dehors des colonnes */}
        {replenishment.comments && (
          <div className="field pt-6">
            <label>{t("common:comments")}</label>
            <Typography className="whitespace-pre-wrap">{replenishment.comments}</Typography>
          </div>
        )}
      </Card>

      {/* Section Parts ordered */}
      {replenishment.parts.length > 0 && (
        <>
          <div className="parts-section-header">
            <Typography variant="h4" className="section-title">
              {t("replenishment:fields.partsOrdered")} ({replenishment.parts.length})
            </Typography>
          </div>

          <Card>
            <Table
              tableHead={[
                { label: t("common:ref:label"), orderKey: "ref" },
                { label: t("piece:label"), orderKey: "label" },
                { label: t("piece:form:fields:quantity:label"), orderKey: "quantity" },
                { label: t("piece:form:fields:unity:label"), orderKey: "unity" },
              ]}
              enableRowLinks={false}
            >
              {replenishment.parts.map((part, i) => {
                const isLast = i === replenishment.parts.length - 1;
                const classes = isLast ? "p-2" : "p-2 border-b border-blue-gray-50";

                return (
                  <tr key={`${part.part.id}-${i}`}>
                    <td className={classes}>
                      <Tooltip content={part.part.ref}>
                        <Typography variant="small" className="truncate block max-w-[10rem]">
                          {part.part.ref}
                        </Typography>
                      </Tooltip>
                    </td>
                    <td className={classes}>
                      <Tooltip content={part.part.label}>
                        <Typography variant="small" className="truncate block max-w-xs">
                          {part.part.label}
                        </Typography>
                      </Tooltip>
                    </td>
                    <td className={classes}>
                      <Typography variant="small">{part.quantity}</Typography>
                    </td>
                    <td className={classes}>
                      <Typography variant="small">{part.part.unity}</Typography>
                    </td>
                  </tr>
                );
              })}
            </Table>
          </Card>
        </>
      )}
    </div>
  );
}
